import axios from 'axios';

import { Button } from '@/components/core/Button';
import {
    Dialog,
    DialogBody,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogHeaderDefault,
} from '@/components/core/Dialog';
import { useToast } from '@/hooks/useToast';
import { useTranslation } from '@/hooks/useTranslation';

import { BusinessPartner, contactErrorMessage } from './businessPartner';
import { useDeleteContact } from './businessPartner.queries';

interface DeleteContactDialogProps {
    contact?: BusinessPartner;
    open: boolean;
    onClose: () => void;
}

const DeleteContactDialog = ({ contact, open, onClose }: DeleteContactDialogProps) => {
    const { t } = useTranslation();
    const { toast } = useToast();
    const deleteContact = useDeleteContact();

    const confirm = () => {
        if (contact?.id == null) {
            return;
        }
        deleteContact.mutate(contact.id, {
            onSuccess: () => {
                toast.success('CONTACT_SUCCESSFULLY_DELETED');
                onClose();
            },
            onError: (error) => {
                const data = axios.isAxiosError(error) ? error.response?.data : undefined;
                const message = contactErrorMessage(data, t);
                if (message) {
                    toast.error(message);
                }
            },
        });
    };

    return (
        <Dialog open={open} onOpenChange={(next) => { if (!next) onClose(); }}>
            <DialogContent variant="small">
                <DialogHeader>
                    <DialogHeaderDefault>{t('CONFIRM_DELETE')}</DialogHeaderDefault>
                </DialogHeader>
                <DialogBody>
                    <p className="tw-text-t-sm tw-text-t-primary lg:tw-text-t-base">{t('CONFIRM_DELETE_CONTACT')}</p>
                    <p className="tw-mt-2 tw-text-t-sm tw-font-bold tw-text-t-primary lg:tw-text-t-base">{contact?.name}</p>
                </DialogBody>
                <DialogFooter>
                    <Button
                        type="button"
                        variant="destructive"
                        className="confirm-delete"
                        loading={deleteContact.isPending}
                        onClick={confirm}
                    >
                        {t('ACTION.DELETE')}
                    </Button>
                    <Button type="button" variant="secondary" onClick={onClose} disabled={deleteContact.isPending}>
                        {t('CANCEL')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export { DeleteContactDialog };
